import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { Smartphone, SvgIconComponent } from '@mui/icons-material';
import parsePhoneNumberFromString from 'libphonenumber-js/mobile';
import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { SVGIconComponent } from '../../../icons/types';
import { Tooltip } from '../../../tooltip/tooltip';
import { ActionsButton } from '../../../actions-button/actions-button.component';
import { LoadedActionsButtonItem } from '../../../actions-button/actions-button.types';
import { CourierStatusBadge, CourierStatusBadgeDecision } from './courier-status-badge.component';
import { useCourierDetailsPageHeaderStyles } from './courier-details-page-header.styles';

export interface CourierDetailsPageHeaderProps {
  name: string;
  /** The carrier hierarchy shown under the name, top level first. A crumb with a url links there. */
  breadcrumbs: { label: string; url?: string }[];
  /** The carrier's logo, shown beneath the breadcrumbs. */
  carrierImage?: string;
  /** Shown to the right of the carrier logo. Absent drops it, for couriers who never registered. */
  registration?: CourierRegistrationProps;
  status?: {
    label: string;
    Icon: SvgIconComponent | SVGIconComponent;
    color: string;
    /** Approval decisions offered from the badge. Absent or empty renders a plain badge. */
    decisions?: CourierStatusBadgeDecision[];
  };
  mobile?: string;
  externalID?: string;
  /** The username they sign in with, when they have one. */
  username?: string;
  /** The code that goes with the username. Returned in full — it is there to be relayed on. */
  accessCode?: string;
  /** The courier's own photo. */
  image?: string;
  /**
   * One block per network the courier's carrier is granted on, in server order. Empty means the
   * courier can do nothing at any kiosk, not that their capabilities are unknown.
   */
  networkCapabilities: CourierNetworkCapabilityProps[];
  actions: LoadedActionsButtonItem[];
}

/** Each field is already formatted for display; one that is absent is left out. */
export interface CourierRegistrationProps {
  /** The hub registered at. No url when the hub cannot be read, so the name shows unlinked. */
  hub?: { name: string; url?: string };
  /** Registered at the hub's kiosk rather than on the courier app. */
  isKiosk: boolean;
  distance?: string;
  time?: string;
}

export interface CourierNetworkCapabilityProps {
  networkID: string;
  networkName: string;
  transactionTypes: { Icon: SVGIconComponent; label: string; transactionTypeLabels: string[] }[];
  /** Scope and, where it applies, who holds the grant — shown on hovering the network name. */
  tooltipLines: string[];
}

/**
 * A courier's page header, its own component because it reads differently to a user's: the carrier
 * hierarchy takes the place of the subtitle and address, and capabilities are per network rather
 * than a single set — a courier's permissions come from their carrier's network grants.
 */
export function CourierDetailsPageHeader({
  name,
  breadcrumbs,
  carrierImage,
  registration,
  status,
  mobile,
  externalID,
  username,
  accessCode,
  image,
  networkCapabilities,
  actions
}: CourierDetailsPageHeaderProps) {
  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useCourierDetailsPageHeaderStyles();

  return (
    <Grid container sx={{ flexDirection: isSM ? 'column' : 'row' }} className={classes.root}>
      <Grid
        container
        size={{
          md: 4
        }}
        sx={{
          justifyContent: 'space-between',
          flexDirection: 'column'
        }}
      >
        <Grid container sx={{ flexDirection: 'column' }}>
          <Grid className={classes.courierName}>
            <h2>{name}</h2>
          </Grid>
          {breadcrumbs.length > 0 ? (
            <Grid>
              <p className={classes.breadcrumbs}>
                {breadcrumbs.map((crumb, index) => (
                  <Fragment key={crumb.label}>
                    {index > 0 ? <span className={classes.breadcrumbSeparator}>&gt;</span> : null}
                    {crumb.url ? (
                      <Link className={classes.breadcrumbLink} to={crumb.url}>
                        {crumb.label}
                      </Link>
                    ) : (
                      <span>{crumb.label}</span>
                    )}
                  </Fragment>
                ))}
              </p>
            </Grid>
          ) : null}
          {carrierImage || registration ? (
            <Grid className={classes.carrierRow}>
              {carrierImage ? (
                <img
                  className={classes.carrierImage}
                  src={carrierImage}
                  alt={breadcrumbs[breadcrumbs.length - 1]?.label}
                />
              ) : null}
              {registration ? <CourierRegistration registration={registration} /> : null}
            </Grid>
          ) : null}
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        size={{
          md: 4
        }}
        sx={{
          justifyContent: 'space-between',
          flexDirection: 'column'
        }}
      >
        {/* Couriers badge their status in the same slot and the same style as every other user
            type, so the two details pages never disagree about where a status lives. */}
        <Grid>
          {status ? (
            <CourierStatusBadge
              color={status.color}
              label={status.label}
              Icon={status.Icon}
              decisions={status.decisions}
            />
          ) : null}
        </Grid>
        {/* Capabilities sit in the same slot as a user's, but headed by the network they apply in:
            the same courier can hold different permissions in each. */}
        {networkCapabilities.map((capability) => (
          <Grid key={capability.networkID} container sx={{ flexDirection: 'column' }}>
            {/* The network name carries the scope, each icon its own transaction types, so the two
                kinds of detail never share one tooltip. Both size to their text rather than the
                wrapper's fixed 320px; MuiTooltip's own maxWidth still wraps a long project list. */}
            <Tooltip title={capability.tooltipLines.join(' \u00b7 ')} placement="bottom-start" width="auto">
              <Grid className={classes.capabilities}>
                <span>For {capability.networkName}</span>
              </Grid>
            </Tooltip>
            <Grid container direction="row" spacing={1}>
              {capability.transactionTypes.length === 0 ? (
                <Grid>
                  <span className={classes.info}>No capabilities</span>
                </Grid>
              ) : (
                capability.transactionTypes.map((type) => (
                  <Grid key={type.label}>
                    {/* The icons collapse deliver and collect into one, so hovering names both. The
                        span is here because the icons are plain components and hold no ref. */}
                    <Tooltip title={type.transactionTypeLabels.join(' \u00b7 ')} placement="bottom" width="auto">
                      <span>
                        <type.Icon className={classes.capabilitiesIcon} />
                      </span>
                    </Tooltip>
                  </Grid>
                ))
              )}
            </Grid>
          </Grid>
        ))}
        <Grid container>
          <Grid>
            {/* A contact row the courier hasn't got is dropped entirely rather than rendered as a
                bare icon with nothing beside it. */}

            {mobile || externalID || username || accessCode ? (
              <div className={classes.signIn}>
                {mobile ? (
                  <p className={classes.signInRow}>
                    <Smartphone className={classes.signInIcon} /> {formatMobile(mobile)}
                  </p>
                ) : null}
                {externalID ? <p className={classes.signInRow}>ID: {externalID}</p> : null}
                {username ? <p className={classes.signInRow}>Username: {username}</p> : null}
                {accessCode ? <p className={classes.signInRow}>Access code: {accessCode}</p> : null}
              </div>
            ) : null}
            {image ? <img className={classes.courierImage} src={image} alt={name} /> : null}
          </Grid>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        size={{
          md: 4
        }}
        sx={{
          justifyContent: 'space-between',
          flexDirection: 'column'
        }}
      >
        <Grid container direction="row" spacing={2}>
          {/* ActionsButton renders nothing when the array is empty. */}
          <div className={classes.actionButtonBox}>
            <ActionsButton itemArray={actions} />
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
}

/**
 * "Registered at <hub>" from a kiosk, or "Registered on mobile near <hub>" from the courier app, with
 * the distance (mobile only, as a kiosk is always at its hub) and time on hover. The tooltip sits on the link, or on a
 * focusable span without one, so keyboard users reach it too. No tooltip when there is nothing to add.
 */
function CourierRegistration({ registration }: { registration: CourierRegistrationProps }) {
  const classes = useCourierDetailsPageHeaderStyles();
  const { hub, isKiosk, distance, time } = registration;
  const details = [distance && !isKiosk ? `~${distance} from hub` : undefined, time].filter(Boolean).join(' \u00b7 ');

  const label = hub?.url ? (
    <Link className={classes.registrationLink} to={hub.url}>
      {hub.name}
    </Link>
  ) : (
    <span tabIndex={details ? 0 : undefined}>{hub ? hub.name : 'Registered'}</span>
  );

  return (
    <p className={classes.registration}>
      {hub ? (isKiosk ? 'Registered at ' : 'Registered on mobile near ') : null}
      <Tooltip title={details} placement="bottom-start" width="auto">
        {label}
      </Tooltip>
    </p>
  );
}

/** Falls back to the stored number whenever it cannot be parsed, rather than showing nothing. */
function formatMobile(mobile: string): string {
  try {
    return parsePhoneNumberFromString(mobile)?.formatInternational() ?? mobile;
  } catch (e) {
    return mobile;
  }
}
